<template>
  <v-dialog
    v-model="dialog"
    :max-width="calcMaxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        :aria-label="title"
        :title="title"
        v-bind="attrs"
        @click="onButtonActivate"
        v-on="on"
      >
        <template v-if="buttonTitle.startsWith('mdi-')">
          <v-icon>
            {{ buttonTitle }}
          </v-icon>
        </template>
        <template v-else>
          {{ buttonTitle }}
        </template>
      </v-btn>
    </template>

    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
    >
      <v-card>
        <v-card-title>
          <v-row>
            <v-col>
              {{ !!item ? 'Edit' : 'Create' }}
            </v-col>
            <v-col>
              <v-select
                v-model="cls"
                :items="classes"
                :rules="[(v) => !!v || 'Required']"
                label="Class"
              />
            </v-col>
            <v-col v-if="!['REFERENCE', 'GROUP', 'ALERT'].includes(cls)">
              <v-select
                v-model="type"
                :items="classOperationTypes[cls]"
                :rules="[(v) => !!v || 'Required']"
                label="Type"
              />
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row
              v-if="['SMILES', 'SMARTS'].includes(type)"
              dense
            >
              <v-col>
                <Ketcher
                  v-model="rxn"
                  lookup
                  @found="setFound"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col v-if="type === 'METHOD' || cls === 'REFERENCE'">
                <v-select
                  v-model="editItem.value"
                  :items="operations"
                  :rules="[(v) => !!v || 'Required']"
                  label="Operation"
                />
              </v-col>

              <template v-else-if="!['SMILES', 'SMARTS'].includes(type)">
                <v-col>
                  <v-text-field
                    v-if="['GROUP', 'ALERT'].includes(cls)"
                    v-model="editItem.name"
                    :rules="[(v) => !!v || 'Required']"
                    label="Name"
                  />
                  <v-text-field
                    v-else-if="type !== 'PROPERTY'"
                    v-model="editItem.value"
                    :rules="[(v) => !!v || 'Required']"
                    label="Value"
                  />
                </v-col>
              </template>

              <v-col v-if="!['METHOD', 'REFERENCE'].includes(type)">
                <v-text-field
                  v-model="editItem.text"
                  label="Title"
                />
              </v-col>

              <v-col cols="2">
                <v-select
                  v-model="editItem.logicType"
                  :items="logicItems(cls)"
                  :rules="[(v) => !!v || 'Required']"
                  label="Logic"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="4">
                <v-text-field
                  v-model="editItem.category"
                  label="Category"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="type === 'HAZARD'"
              dense
            >
              <v-col cols="12">
                <v-select
                  v-model="editItem.endpoint.id"
                  :items="hazardEndpoints"
                  :rules="[(v) => !!v || 'Required']"
                  label="Endpoint"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="editItem.endpoint.scores"
                  :items="['L', 'M', 'H', 'VH']"
                  chips
                  deletable-chips
                  label="Score"
                  multiple
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="editItem.endpoint.authorities"
                  :items="['Authoritative', 'Screening', 'QSAR Model']"
                  chips
                  deletable-chips
                  label="Authority"
                  multiple
                />
              </v-col>
            </v-row>

            <v-row
              v-if="type === 'PROPERTY'"
              dense
            >
              <v-col>
                <v-select
                  v-model="editItem.property.name"
                  :items="['MW', 'logP', 'WS']"
                  :rules="[(v) => !!v || 'Required']"
                  label="Property"
                />
              </v-col>
              <v-col>
                <v-select
                  v-model="editItem.property.operator"
                  :items="itemsOperator"
                  label="Operator"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="editItem.property.value1"
                  label="Value"
                />
              </v-col>
              <v-col v-if="editItem.property.operator === 'RANGE'">
                <v-text-field
                  v-model="editItem.property.value2"
                  label="Value 2"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editItem.description"
                  label="Description"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <span
            v-if="message"
            style="color: red; font-size: larger"
          >
            {{ message }}
          </span>
          <v-spacer />
          <v-btn
            @click="dialog = false"
            aria-label="Cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="!changed"
            @click="onSave"
            aria-label="Save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import KetcherEditor from '@/components/ketcher-editor/KetcherEditor.vue'
import DialogBase from '@/components/dialogs/DialogBase.vue'
import { ALERTS_URL, getErrorMessage, SERVICES_URL } from '@/main'

export default {
  components: { Ketcher: KetcherEditor },

  mixins: [DialogBase],

  props: {
    item: {
      type: Object,
      required: false,
    },
    global: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: true,
      rxn: null,
      changed: false,
      cls: null,
      type: null,
      editItem: {
        cls: null,
        type: null,
        text: null,
        description: null,
        value: null,
        endpoint: {
          id: null,
          scores: [],
          authorities: [],
        },
        property: {
          name: null,
          operator: null,
          value1: null,
          value2: null,
        },
      },
      classOperationTypes: {
        CHECK: ['METHOD', 'SMILES', 'SMARTS', 'TOXPRINT', 'HAZARD', 'PROPERTY'],
      },
      availableOperations: null,
      hazardEndpoints: null,
      itemsOperator: [
        { value: 'EQ', text: '==' },
        { value: 'LT', text: '<' },
        { value: 'LE', text: '<=' },
        { value: 'GT', text: '>' },
        { value: 'GE', text: '>=' },
        { value: 'RANGE', text: '[..]' },
      ],
    }
  },

  computed: {
    groupId() {
      return this.$route.params.id
    },
    ALERTS_URL() {
      return ALERTS_URL
    },
    calcMaxWidth() {
      return ['SMILES', 'SMARTS'].includes(this.type) ? '900' : this.maxWidth
    },
    operations() {
      let query = this.cls === 'REFERENCE' ? 'GROUP' : this.cls
      return this.availableOperations.filter((o) => o['@c'] && o['@c'].toUpperCase().endsWith(query))
    },
    classes() {
      return this.global ? ['GROUP', 'ALERT'] : ['CHECK', 'REFERENCE']
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.message = null
        if (!this.item) {
          this.editItem = {
            cls: null,
            type: null,
            text: null,
            description: null,
            value: null,
            endpoint: {
              id: null,
              scores: [],
              authorities: [],
            },
            property: {
              name: null,
              operator: null,
              value1: null,
              value2: null,
            },
          }
          this.cls = this.global ? 'GROUP' : 'CHECK'
        } else {
          this.editItem = Object.assign({}, this.item)
          if (this.editItem['@c'] === '.Check') this.cls = 'CHECK'
          else if (this.editItem['@c'] === '.HazardCheck') {
            this.cls = 'CHECK'
            this.type = 'HAZARD'
          } else if (this.editItem['@c'] === '.PropertyCheck') {
            this.cls = 'CHECK'
            this.type = 'PROPERTY'
          } else if (this.editItem['@c'] === '.GroupReference') this.cls = 'REFERENCE'
          else if (this.editItem['@c'] === '.LibraryGroup') this.cls = 'GROUP'
          else if (this.editItem['@c'] === '.Alert') this.cls = 'ALERT'

          this.type = this.editItem.type

          if (['SMILES', 'SMARTS'].includes(this.type)) {
            if (this.editItem.value.includes('\n')) {
              this.rxn = this.editItem.value
              this.changed = false
            } else {
              this.$axios
                .get(`${SERVICES_URL}/layout?smiles=${encodeURIComponent(this.editItem.value)}`)
                .then((response) => {
                  this.rxn = response.data
                  this.changed = false
                })
            }
          }
        }
      }
    },
    rxn() {
      this.changed = true
      this.editItem.value = this.rxn
    },
    editItem: {
      handler: function () {
        this.changed = true
        if (this.type === 'HAZARD') {
          this.editItem.value = `${this.editItem.endpoint.id}; ${this.editItem.endpoint.scores.join(',')}; ${this.editItem.endpoint.authorities.join(',')}`
        } else if (this.type === 'PROPERTY') {
          let v = `${this.editItem.property.name}; ${this.editItem.property.operator}; ${this.editItem.property.value1}`
          if (this.editItem.property.operator === 'RANGE') v += `; ${this.editItem.property.value2}`
          this.editItem.value = v
        }
      },
      deep: true,
    },
  },

  methods: {
    logicItems(cls) {
      if (cls === 'CHECK' || cls === 'REFERENCE') return [{ value: 'NONE', text: '' }, 'NOT']
      else
        return [
          { value: 'OR', text: 'ANY' },
          { value: 'AND', text: 'ALL' },
        ]
    },
    setFound(record) {
      this.editItem.text = record.name
    },
    onSave() {
      if (this.changed && this.$refs.form.validate()) {
        if (this.type === 'HAZARD') {
          this.editItem['@c'] = '.HazardCheck'
          this.editItem.type = this.type
        } else if (this.type === 'PROPERTY') {
          this.editItem['@c'] = '.PropertyCheck'
          this.editItem.type = this.type
        } else if (this.cls === 'CHECK') {
          this.editItem['@c'] = '.Check'
          this.editItem.type = this.type
        } else if (this.cls === 'REFERENCE') {
          this.editItem['@c'] = '.GroupReference'
          this.editItem.type = 'REFERENCE'
        } else if (this.cls === 'GROUP') {
          this.editItem['@c'] = '.LibraryGroup'
          this.editItem.type = 'GROUP'
        } else if (this.cls === 'ALERT') {
          this.editItem['@c'] = '.Alert'
          this.editItem.type = 'GROUP'
        }

        if (['SMILES', 'SMARTS'].includes(this.editItem.type)) {
          this.$axios
            .post(`${SERVICES_URL}/convert`, {
              content: this.rxn,
              type: 'mol2smiles',
            })
            .then((response) => {
              this.editItem.value = response.data
              this.$emit('onSave', this.editItem)
              this.dialog = false
            })
            .catch((error) => {
              this.uploading = false
              this.message = getErrorMessage(error)
            })
        } else {
          if (this.type === 'METHOD' || this.cls === 'REFERENCE')
            this.editItem.text = this.operations.find((o) => o.value === this.editItem.value).text
          this.$emit('onSave', this.editItem)
          this.dialog = false
        }
      }
    },
    getData() {
      this.$axios.get(`${ALERTS_URL}/operations`).then((response) => {
        this.availableOperations = response.data.operations
        this.hazardEndpoints = response.data.hazardEndpoints
      })
    },
  },

  mounted() {
    this.getData()
  },
}
</script>
